import Card from '@app/components/common/Card';
import Dropdown from '@app/components/common/Dropdown';
import Grid from '@app/components/common/Grid';
import MoneyInput from '@app/components/common/MoneyInput';
import {
  actionCreatorsMenu,
  EItemDimensionsSellBy,
  IComboMenuTypeVersion,
  IMassUpdate,
  IMenuTypeVersion,
  ITEM_SELL_BY_OPTIONS,
  ITEM_WEIGHT_OPTIONS,
  itemSellByDropdownFactory,
  itemWeightDropdownFactory,
  MassUpdateValues,
  SectionId,
  taxVolumeUnitDropdownFactory,
  unitToValue,
  valueToUnit,
} from '@westondev/tableturn-core';
import { useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

const sectionId = SectionId.BASIC;

const gridItemSizing = {
  mb: 15,
  sm: 5,
  md: 5,
  xl: 3,
};

interface IDimensionsSubsection extends WithTranslation {
  menuTypeVersionId: number;
  itemData?: IMenuTypeVersion | IComboMenuTypeVersion | IMassUpdate;
  isMassUpdate?: boolean;
}

const MASS_UPDATE_PRICE_BREAKPOINTS = {
  mb: 12,
  md: 6,
  lg: 3,
};

const MASS_UPDATE_PRICES_GAP = {
  md: 20,
  lg: 40,
  xl: 80,
};

const DimensionsSubsection = ({
  t,
  menuTypeVersionId,
  itemData,
  isMassUpdate,
}: IDimensionsSubsection) => {
  // Redux
  const { updateItem: setValue } = bindActionCreators(
    actionCreatorsMenu,
    useDispatch(),
  );

  const volumeOptions = useMemo(
    () => taxVolumeUnitDropdownFactory(isMassUpdate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isMassUpdate],
  );

  const sellByOptions = useMemo(
    () => itemSellByDropdownFactory(isMassUpdate || false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isMassUpdate],
  );

  const weightOptions = useMemo(
    () => itemWeightDropdownFactory(isMassUpdate || false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isMassUpdate],
  );

  const dimensionsSellBy = (itemData as IMassUpdate)?.dimensionsSellBy;
  const dimensionsWeightUnit = (itemData as IMassUpdate)?.dimensionsWeightUnit;

  const sellByValue = useMemo(
    () =>
      dimensionsSellBy === MassUpdateValues.NO_CHANGES
        ? -1
        : ITEM_SELL_BY_OPTIONS.indexOf(dimensionsSellBy) + 1,
    [dimensionsSellBy],
  );

  const weightValue = useMemo(
    () =>
      dimensionsWeightUnit === MassUpdateValues.NO_CHANGES
        ? -1
        : ITEM_WEIGHT_OPTIONS.indexOf(dimensionsWeightUnit) + 1,
    [dimensionsWeightUnit],
  );

  const isSellByWeight = dimensionsSellBy === EItemDimensionsSellBy.WEIGHT;
  const isSellByVolume = dimensionsSellBy === EItemDimensionsSellBy.VOLUME;

  return (
    <Card.SubCard title={t('menuScreen.itemDetails.dimensions.title')}>
      <Grid
        columnGap={isMassUpdate ? MASS_UPDATE_PRICES_GAP : 20}
        rowGap={20}
        columns={isMassUpdate ? undefined : 15}>
        <Grid.Item
          {...gridItemSizing}
          csx={{ justifyContent: 'center' }}
          {...(isMassUpdate ? MASS_UPDATE_PRICE_BREAKPOINTS : {})}>
          <Dropdown
            isDisabled
            label={t('app.modals.itemsDiff.dimensions.dimensionsSellBy')}
            placeholder={t('commonTexts.placeholderDropdown')}
            data={sellByOptions}
            value={sellByValue}
            onChange={newValue =>
              setValue(
                {
                  dimensionsSellBy: ITEM_SELL_BY_OPTIONS[newValue - 1],
                },
                sectionId,
                menuTypeVersionId,
              )
            }
          />
        </Grid.Item>
        <Grid.Item
          {...gridItemSizing}
          csx={{ justifyContent: 'center' }}
          {...(isMassUpdate ? MASS_UPDATE_PRICE_BREAKPOINTS : {})}>
          <MoneyInput
            info={t('app.modals.itemsDiff.dimensions.dimensionsVolumePerUnit')}
            label={t('app.modals.itemsDiff.dimensions.dimensionsVolumePerUnit')}
            value={(itemData as IMenuTypeVersion)?.dimensionsVolumePerUnit || 0}
            onValueChange={dimensionsVolumePerUnit =>
              setValue(
                { dimensionsVolumePerUnit },
                sectionId,
                menuTypeVersionId,
              )
            }
            onChangeEmpty={
              isMassUpdate
                ? dimensionsVolumePerUnit =>
                    setValue(
                      {
                        dimensionsVolumePerUnit:
                          dimensionsVolumePerUnit === null
                            ? MassUpdateValues.NO_CHANGES
                            : Number(dimensionsVolumePerUnit),
                      },
                      sectionId,
                      menuTypeVersionId,
                    )
                : undefined
            }
            prefix=""
            suffix=""
            precision={2}
            disabled={isSellByWeight || isSellByVolume}
          />
        </Grid.Item>
        <Grid.Item
          {...gridItemSizing}
          csx={{ justifyContent: 'center' }}
          {...(isMassUpdate ? MASS_UPDATE_PRICE_BREAKPOINTS : {})}>
          <Dropdown
            label={t('app.modals.itemsDiff.dimensions.dimensionsVolumeUnit')}
            placeholder={t('commonTexts.placeholderDropdown')}
            data={volumeOptions}
            value={
              itemData
                ? (itemData as IMassUpdate)?.dimensionsVolumeUnit ===
                  MassUpdateValues.NO_CHANGES
                  ? -1
                  : unitToValue[
                      (itemData as IMenuTypeVersion)?.dimensionsVolumeUnit
                    ]
                : 0
            }
            onChange={dimensionsVolumeUnit =>
              setValue(
                {
                  dimensionsVolumeUnit: valueToUnit[dimensionsVolumeUnit],
                },
                sectionId,
                menuTypeVersionId,
              )
            }
            isDisabled={isSellByWeight}
          />
        </Grid.Item>
        <Grid.Item
          {...gridItemSizing}
          csx={{ justifyContent: 'center' }}
          {...(isMassUpdate ? MASS_UPDATE_PRICE_BREAKPOINTS : {})}>
          <MoneyInput
            info={t('app.modals.itemsDiff.dimensions.dimensionsWeightPerUnit')}
            label={t('app.modals.itemsDiff.dimensions.dimensionsWeightPerUnit')}
            value={
              itemData
                ? (itemData as IMassUpdate)?.dimensionsWeightPerUnit ===
                  MassUpdateValues.NO_CHANGES
                  ? null
                  : (itemData as IMenuTypeVersion)?.dimensionsWeightPerUnit
                : 0
            }
            onValueChange={dimensionsWeightPerUnit =>
              setValue(
                { dimensionsWeightPerUnit },
                sectionId,
                menuTypeVersionId,
              )
            }
            onChangeEmpty={
              isMassUpdate
                ? dimensionsWeightPerUnit =>
                    setValue(
                      {
                        dimensionsWeightPerUnit:
                          dimensionsWeightPerUnit === null
                            ? MassUpdateValues.NO_CHANGES
                            : Number(dimensionsWeightPerUnit),
                      },
                      sectionId,
                      menuTypeVersionId,
                    )
                : undefined
            }
            prefix=""
            suffix=""
            precision={2}
            disabled={isSellByWeight || isSellByVolume}
          />
        </Grid.Item>
        <Grid.Item
          {...gridItemSizing}
          csx={{ justifyContent: 'center' }}
          {...(isMassUpdate ? MASS_UPDATE_PRICE_BREAKPOINTS : {})}>
          <Dropdown
            label={t('app.modals.itemsDiff.dimensions.dimensionsWeightUnit')}
            placeholder={t('commonTexts.placeholderDropdown')}
            data={weightOptions}
            value={weightValue}
            onChange={newValue =>
              setValue(
                {
                  dimensionsWeightUnit: ITEM_WEIGHT_OPTIONS[newValue - 1],
                },
                sectionId,
                menuTypeVersionId,
              )
            }
            isDisabled={isSellByVolume}
          />
        </Grid.Item>
      </Grid>
    </Card.SubCard>
  );
};
export default DimensionsSubsection;
